/* eslint-disable react-hooks/rules-of-hooks */
// React&NextJS
import React, { useEffect, useState } from 'react'
import { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'
// Context&Auth
import { useAuth } from '../../../context/UserContext'
// 3rd party libraries
import { AnimatePresence, motion } from 'framer-motion'
import { usePlausible } from 'next-plausible'
// Services&Helper functions
import firestoreReadService from '../../../services/read'
import helperFunctions from '../../../utils/helperFunctions'
import ReadOnlyObituaryPlate from '../../../components/components/plate/ReadObituaryPlate'
import memoryHelperFunctions from '../../../utils/memoryHelperFunctions'
// Types
import {
    Condolence,
    Memory as memoryType,
    ObituaryPlate,
} from '../../../types/types'
// Styles
import s from '../../../styles/pages/Memory.module.scss'
// Components
import AcceptInvite from '../../../components/components/acceptInvite/AcceptInvite'
import Announcements from '../../../components/components/announcements/Announcements'
import { Button } from '../../../components/core/button/Button'
import Condolences from '../../..//components/components/condolences/Condolences'
import { Container } from '../../../components/core/container/Container'
import ImageBlob from '../../../components/components/imageBlob/ImageBlob'
import ObituaryCard from '../../../components/cards/obituaryCard/ObituaryCard'
import { Section } from '../../../components/core/section/Section'
import { Tabs } from '../../../components/core/tabs/Tabs'
import { Text } from '../../../components/core/text/Text'
import { WhiteBox } from '../../../components/core/whiteBox/WhiteBox'
import MemoryNotFound from '../../memoryNotFound'
import MemoryMeta from '../../../components/components/memoryMeta/MemoryMeta'
import ShareModal from '../../../components/components/modals/ShareModal'
import MemoryBio from '../../../components/components/memBio/MemoryBio'
import MemoryGallery from '../../../components/components/memoryGallery/MemoryGallery'
import { Icon } from '../../../components/core/icon/Icon'
import BackToTop from '../../../components/components/backToTop/BackToTop'
import { DefaultBio } from '../../../components/components/defaultBio/DefaultBio'
import AuthModal from '../../../components/components/modals/AuthModal'

export const getServerSideProps: GetServerSideProps = async ({
    res,
    params,
    query,
}) => {
    res.setHeader('Cache-Control', 'max-age=0, s-maxage=1, must-revalidate')
    const { shared, obit } = query
    //TODO: refactor to use either await or then?

    const memoryData = await firestoreReadService
        .getMemoryById(params.mid as string, false, false)
        .then((data) => {
            return data ? JSON.stringify(data) : null
        })
        .catch((err) => console.error(err))
    return {
        props: {
            mid: params.mid,
            memoryData,
            shared: shared || '',
            obit: obit || null,
        },
    }
}

type Props = {
    mid: string
    memoryData: string
    shared: string
    obit: string
}

function Memory({ mid, memoryData, shared, obit }: Props) {
    if (!memoryData) return <MemoryNotFound />
    const { query } = useRouter()
    const { user } = useAuth()
    const router = useRouter()
    const memory: memoryType = JSON.parse(memoryData)
    const [owner, setOwner] = useState<boolean>(false)

    const shareType = shared
    const isFollowing = memory.followers?.includes(user?.uid)

    const [tab, setTab] = useState<String>('Minningargreinar')
    const [tabPos, setTabPos] = useState<number>()
    const [condolences, setCondolences] = useState(memory.condolences)
    const [shareCards, setShareCards] = useState(memory.shareCards)
    const [, setIsLoaded] = useState(false)
    const [invite, setInvite] = useState(false)
    const [bioText, setBioText] = useState<JSX.Element | JSX.Element[]>(null)
    const [openModal, setOpenModal] = useState<boolean>(false)
    const [openAuth, setOpenAuth] = useState<boolean>(false)
    const [backToTop, setbackToTop] = useState(false)
    const [following, setFollowing] = useState(isFollowing || false)

    const birthDate = helperFunctions.getDate(memory.birth)
    const deathDate = helperFunctions.getDate(memory.death)
    const plausible = usePlausible()

    useEffect(() => {
        // Check if this is an invitation link and if user is logged in
        setInvite(query?.invite ? true : false)
        if (invite) {
            if (!user) {
                router.push(`/sign-up?memory=${memory.mid}`)
            } else if (query?.invite && user) {
                setIsLoaded(true)
            }
        }
    }, [invite, user])

    // Check if logged in user is owner of mem page.
    useEffect(() => {
        if (user) {
            let own = user.memories.find((m) => m.id === mid)
            setOwner(own ? true : false)
        } else {
            setOwner(false)
            following && setFollowing(false)
        }
    }, [user])

    useEffect(() => {
        let timeout = null
        let counter = 0
        const func = async () => {
            counter += 3
            if (counter === 18) {
                console.log('Refetch cards!')
                memoryHelperFunctions.refetchCards(memory, mid)
            }
            const mem = await firestoreReadService.getMemoryById(
                mid,
                false,
                false
            )

            if (mem.shareCards?.memory) {
                setShareCards(mem.shareCards)
            } else {
                timeout = setTimeout(() => {
                    func()
                }, 3000)
            }
        }
        if (!memory.shareCards?.memory) {
            func()
        }

        return () => {
            if (timeout) clearTimeout(timeout)
        }
    }, [])

    useEffect(() => {
        // const offset = window.innerWidth < 720 ? 650 : 350
        // setbackToTop(window.scrollY >= offset ? true : false)
        const handleScroll = () => {
            const tabsY = document
                .getElementById('tabs')
                .getBoundingClientRect().top

            setbackToTop(tabsY < window.innerHeight / 2)
        }
        handleScroll()
        window.addEventListener('scroll', handleScroll)
        return () => {
            window.removeEventListener('scroll', handleScroll)
        }
    }, [])

    useEffect(() => {
        // create the biotext
        setBioText(createBioText())
        // Get hash from uri to set tabs and scroll to element/component
        const tabElem = document.getElementById('tabs')
        setTabPos(tabElem.getBoundingClientRect().top + window.scrollY)
        if (window.location.hash) {
            const hash = window.location.hash.replace('#', '')
            // check if anchor hashtag is condolences or obituary id
            if (hash === 'condolences') {
                var topPos =
                    tabElem.getBoundingClientRect().top + window.scrollY
                setTab('Kveðjur')
                window.scroll({
                    top: topPos - 100,
                    behavior: 'smooth',
                })
                setIsLoaded(true)
            } else {
                const obituary = document.getElementById(hash)
                const obitLocation = obituary?.getBoundingClientRect().top || 0
                window.scroll({
                    left: 0,
                    top: obitLocation - 100,
                    behavior: 'smooth',
                })
            }
        }
    }, [])

    const handleReadMoreBio = () => {
        setTab('Æviágrip')
        window.scrollTo({
            top: tabPos - 50,
            behavior: 'smooth',
        })
    }

    //TODO: check if this can be handled by a component
    const createBioText = () => {
        if (memory.biography) {
            const bioNodes = memory.biography
            const bioHtml = ReadOnlyObituaryPlate({
                nodes: bioNodes,
            })

            return bioHtml
        }
        return []
    }

    const addCondolence = (c: Condolence) => {
        const condList = condolences
        condList.unshift(c)
        setCondolences([...condList])
    }

    const notEditor = () => {
        return (
            !user || (!memory.editors.includes(user.uid) && following !== null)
        )
    }

    const changeFollowState = () => {
        if (user) {
            const followState = !following
            setFollowing((prev) => !prev)
            memoryHelperFunctions.changeFollowState(followState, mid, user.uid)
        } else {
            setOpenAuth((prev) => !prev)
        }
    }

    const getTabs = () => {
        const tabsList = ['Minningargreinar']
        tabsList.push('Æviágrip')
        tabsList.push('Kveðjur')
        return tabsList
    }

    const getObituaries = () => {
        const reducer = (
            prevVal: Array<ObituaryPlate>,
            currVal: ObituaryPlate
        ) => {
            if (currVal.visible) {
                prevVal.push(currVal)
            }
            return prevVal
        }

        const obituaries = memory.obituaries?.reduce(reducer, []) || []
        return obituaries
    }

    const clickCondolenceHandler = () => {
        setTab('Kveðjur')
        const condPos =
            document.getElementById('tabs').getBoundingClientRect().top +
            window.scrollY
        window.scroll({
            top: condPos - 50,
            behavior: 'smooth',
        })
    }

    const tabs = () => {
        const obituaries = getObituaries()
        if (tab === 'Minningargreinar') {
            return (
                <>
                    {obituaries.length > 0 ? (
                        <>
                            {memory.obituaries.map(
                                (o: ObituaryPlate, i: number) => {
                                    if (o.visible) {
                                        return (
                                            <ObituaryCard
                                                key={i}
                                                oid={o.oid}
                                                header={o.title}
                                                headerImage={o.mainImage}
                                                className={s.tab}
                                                jsxObituary={o.text}
                                                gallery={o.gallery}
                                                mid={mid}
                                            />
                                        )
                                    }
                                }
                            )}
                        </>
                    ) : (
                        <ObituaryCard
                            header="Ertu með minningargrein?"
                            mid={mid}
                            className={s.tab}
                            isDefault={true}
                        />
                    )}
                </>
            )
        } else if (tab === 'Kveðjur') {
            return (
                <Condolences
                    condolences={condolences}
                    mid={mid}
                    addCondolence={addCondolence}
                    className={s.tab}
                />
            )
        }
        // Æviágrip
        else if (tab === 'Æviágrip') {
            if (memory.biography) {
                return <WhiteBox className={s.tab}>{createBioText()}</WhiteBox>
            } else {
                return <DefaultBio className={s.tab} owner={owner} mid={mid} />
            }
        }
        return
    }

    return (
        <>
            {user && memory.invitedEmails && (
                <AcceptInvite setFollowing={setFollowing} memory={memory} />
            )}
            <ShareModal
                isOpen={openModal}
                setOpenModal={setOpenModal}
                title="Hverju viltu deila?"
                memory={memory}
                isLoading={!shareCards?.memory}
            />
            <AuthModal
                isOpen={openAuth}
                closeModal={() => setOpenAuth(false)}
            />
            <Section>
                <MemoryMeta memory={memory} shareType={shareType} obit={obit} />
                <Container>
                    <AnimatePresence>
                        <motion.div
                            key={mid}
                            className={s.image}
                            transition={{
                                type: 'interia',
                                stiffness: 1,
                                default: { duration: 0.5 },
                            }}
                            initial={{
                                opacity: 0,
                                transform: 'translateX(30px)',
                            }}
                            animate={{
                                opacity: 1,
                                transform: 'translateX(0px)',
                            }}
                            exit={{ opacity: 0, transform: 'translateX(30px)' }}
                        >
                            <ImageBlob
                                align
                                stayBelow
                                url={memory.profileImg}
                            />
                            <div className={s.buttons}>
                                <Button
                                    className={s.shareBtn}
                                    variant="arrow"
                                    mirror={true}
                                    noPadd
                                    icon="share"
                                    onClick={() =>
                                        setOpenModal((prev) => !prev)
                                    }
                                >
                                    Deila minningarsíðu
                                </Button>
                                {notEditor() && (
                                    <div className={s.follow}>
                                        <Button
                                            className={s.followBtn}
                                            variant="arrow"
                                            mirror={true}
                                            noPadd
                                            icon="heart"
                                            fill={following}
                                            onClick={changeFollowState}
                                        />
                                        <Text
                                            variant="p-small"
                                            align="center"
                                            className={s.info}
                                        >
                                            {following
                                                ? 'Hætta að fylgja'
                                                : 'Fylgja minningarsíðu'}
                                        </Text>
                                    </div>
                                )}
                            </div>
                        </motion.div>

                        <motion.div
                            className={s.profile__details}
                            transition={{
                                type: 'interia',
                                stiffness: 1,
                                default: { duration: 0.3 },
                            }}
                            initial={{
                                opacity: 0,
                            }}
                            animate={{
                                opacity: 1,
                            }}
                            exit={{ opacity: 0 }}
                        >
                            <div>
                                <div className={s.memory_share}>
                                    <Text tag="h1" variant="h2">
                                        {memory.fullName}
                                    </Text>
                                    <div className={s.icons}>
                                        <motion.button
                                            layout
                                            className={s.icon_share}
                                            onClick={() =>
                                                setOpenModal((prev) => !prev)
                                            }
                                        >
                                            <Icon
                                                variant="share"
                                                data-share-button
                                            />
                                        </motion.button>
                                        <div className={s.follow}>
                                            <motion.button
                                                layout
                                                className={s.icon_follow}
                                                onClick={changeFollowState}
                                            >
                                                <Icon
                                                    variant="heart"
                                                    fill={following}
                                                    width="18"
                                                    height="18"
                                                />
                                            </motion.button>
                                            <Text
                                                variant="p-small"
                                                align="center"
                                                className={s.info}
                                            >
                                                {following
                                                    ? 'Hætta að fylgja'
                                                    : 'Fylgja minningarsíðu'}
                                            </Text>
                                        </div>
                                    </div>
                                </div>
                                <Text variant="date">
                                    {birthDate} - {deathDate}
                                </Text>
                                <MemoryBio
                                    memory={memory}
                                    readMore={handleReadMoreBio}
                                    bioText={bioText}
                                />
                            </div>
                            <Announcements memory={memory} />
                            <div className={s.profile__details__buttons}>
                                <Button
                                    to={`/memories/${mid}/obituaries/form`}
                                    onClick={() =>
                                        plausible(
                                            'memoryPage-skrifaMinningargrein-button'
                                        )
                                    }
                                >
                                    Skrifa minningargrein
                                </Button>
                                <Button
                                    onClick={() => {
                                        clickCondolenceHandler()
                                        plausible(
                                            'memoryPage-sendaKveðju-button'
                                        )
                                    }}
                                    variant="arrow"
                                >
                                    Senda kveðju
                                </Button>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </Container>

                <Container>
                    <Tabs
                        id="tabs"
                        className={s.tabs}
                        tabNames={getTabs()}
                        selectedTab={tab}
                        setState={(t: string) => setTab(t)}
                        noFrag
                    />
                </Container>
                <Container>
                    {tabs()}
                    {getObituaries().length >= 2 && (
                        <BackToTop
                            column={11}
                            row={1}
                            span={1000}
                            isActive={backToTop}
                        />
                    )}
                </Container>
                <Container className={s.galleryContainer}>
                    <MemoryGallery gallery={memory.images} />
                </Container>
            </Section>
        </>
    )
}

export default Memory
